import { useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import Footer from "../../marketing/Footer";
import "../../../styles/feature-page.css";

function FeatureIcon({ icon: Icon, className, size = 22 }) {
  if (!Icon) return null;
  return (
    <span className={className} aria-hidden>
      <Icon size={size} strokeWidth={2} />
    </span>
  );
}

export default function FeaturePageLayout({
  featureName,
  eyebrow,
  title,
  subtitle,
  description,
  heroImage,
  heroImageAlt,
  workflowHeading,
  workflowSteps = [],
  benefitsHeading = "Key Benefits",
  benefits = [],
  includedHeading = "What's Included",
  included = [],
  ctaIcon,
  ctaTitle,
  ctaDescription,
  ctaButtonLabel = "Book a Demo",
  demoHref = "/book-demo",
  trialHref = "/",
  theme = "brand",
}) {
  const { pathname } = useLocation();

  // Open every feature page at the top — the router otherwise keeps the
  // scroll position of the page the user came from (e.g. the landing page).
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className={`fp-page${theme ? ` fp-theme-${theme}` : ""}`}>
      <div className="fp-container">
        <nav className="fp-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          {" > "}
          <span>Features</span>
          {" > "}
          <span className="fp-breadcrumb-active">{featureName}</span>
        </nav>

        <section className="fp-hero">
          <div className="fp-hero-copy">
            {eyebrow ? <span className="fp-eyebrow">{eyebrow}</span> : null}
            <h1 className="fp-hero-title">{title}</h1>
            {subtitle ? <h2 className="fp-hero-subtitle">{subtitle}</h2> : null}
            {description ? <p className="fp-hero-text">{description}</p> : null}
            <div className="fp-hero-actions">
              <Link to={demoHref} className="fp-btn fp-btn-primary">
                <Calendar size={16} strokeWidth={2} />
                Book a Demo
              </Link>
              <Link to={trialHref} className="fp-btn fp-btn-secondary">
                <ArrowRight size={16} strokeWidth={2} />
                Start Free Trial
              </Link>
            </div>
          </div>

          {heroImage ? (
            <div className="fp-hero-visual">
              <div className="fp-hero-image-frame">
                <img src={heroImage} alt={heroImageAlt || title} className="fp-hero-image" />
              </div>
            </div>
          ) : null}
        </section>

        {workflowSteps.length > 0 ? (
          <section className="fp-section">
            <h3 className="fp-section-heading">{workflowHeading}</h3>
            <div className="fp-workflow-grid">
              {workflowSteps.map((step, index) => (
                <div key={step.id ?? index} className="fp-workflow-step">
                  <FeatureIcon icon={step.icon} className="fp-step-icon" size={28} />
                  <div className="fp-step-meta">
                    <span className="fp-step-number">{step.id ?? index + 1}</span>
                    <h4 className="fp-step-title">{step.title}</h4>
                  </div>
                  <p className="fp-step-desc">{step.desc}</p>
                  {index < workflowSteps.length - 1 ? <div className="fp-step-connector" /> : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {(benefits.length > 0 || included.length > 0) ? (
          <section className="fp-split">
            {benefits.length > 0 ? (
              <div>
                <h3 className="fp-section-heading fp-left">{benefitsHeading}</h3>
                <div className="fp-benefits-list">
                  {benefits.map((item, i) => (
                    <div key={item.title ?? i} className="fp-benefit-item">
                      <FeatureIcon icon={item.icon} className="fp-benefit-icon" size={20} />
                      <div className="fp-benefit-text">
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {included.length > 0 ? (
              <div>
                <h3 className="fp-section-heading fp-left">{includedHeading}</h3>
                <div className="fp-included-grid">
                  {included.map((item, i) => (
                    <div key={item.title ?? i} className="fp-included-card">
                      <FeatureIcon icon={item.icon} className="fp-included-icon" size={24} />
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="fp-cta">
          <div className="fp-cta-icon-wrap">
            <FeatureIcon icon={ctaIcon} size={40} />
          </div>
          <div className="fp-cta-content">
            <h3>{ctaTitle}</h3>
            <p>{ctaDescription}</p>
          </div>
          <div className="fp-cta-action">
            <Link to={demoHref} className="fp-btn fp-btn-white">
              <Calendar size={16} strokeWidth={2} />
              {ctaButtonLabel}
            </Link>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
